/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor } from 'lexical';

import {
  AutoEmbedOption,
  EmbedConfig,
  EmbedMatchResult,
  LexicalAutoEmbedPlugin,
  URL_MATCHER,
} from '@lexical/react/LexicalAutoEmbedPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { type JSX, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';

import useModal from '../../hooks/useModal';
import Button from '../../ui/Button';
import { DialogActions } from '../../ui/Dialog';
import { INSERT_YOUTUBE_COMMAND } from '../YouTubePlugin';
import { INSERT_CODE_COMMAND } from '@/components/LexicalEditor/plugins/EmbedCodePlugin';
import { Stack, Typography } from '@mui/material';

interface PlaygroundEmbedConfig extends EmbedConfig {
  // Human readable name of the embeded content e.g. Tweet or Google Map.
  contentName: string;

  // Icon for display.
  icon?: JSX.Element;

  // An example of a matching url https://twitter.com/jack/status/20
  exampleUrl: string;

  // For extra searching.
  keywords: Array<string>;

  // Embed a Figma Project.
  description?: string;
}

export const YoutubeEmbedConfig: PlaygroundEmbedConfig = {
  contentName: '유튜브 동영상',

  exampleUrl: 'https://www.youtube.com/watch?v=jNQXAC9IVRw',

  // Icon for display.
  icon: <i className="icon youtube" />,

  insertNode: (editor: LexicalEditor, result: EmbedMatchResult) => {
    editor.dispatchCommand(INSERT_YOUTUBE_COMMAND, result.id);
  },

  keywords: ['youtube', 'video'],

  // Determine if a given URL is a match and return url data.
  parseUrl: async (url: string) => {
    const match =
      /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/.exec(url);

    const id = match ? (match?.[2].length === 11 ? match[2] : null) : null;

    if (id != null) {
      return {
        id,
        url,
      };
    }

    return null;
  },

  type: 'youtube-video',
};

export const CodeEmbedConfig: PlaygroundEmbedConfig = {
  contentName: 'iframe 코드',

  exampleUrl: `<iframe src="https://fnf365.sharepoint.com/sites/CS-2/_layouts/15/Doc.aspx?sourcedoc={aa34f4cd-c0ae-46a9-bb1f-b7450d77794c}&amp;action=embedview&amp;wdAr=1.8008474576271187" width="476px" height="288px" frameborder="0">포함된 <a target="_blank" href="https://office.com">Microsoft Office</a> 프레젠테이션, 제공: <a target="_blank" href="https://office.com/webapps">Office</a></iframe>`,

  // Icon for display.
  icon: <i className="icon code" />,

  insertNode: (editor: LexicalEditor, result: EmbedMatchResult) => {
    editor.dispatchCommand(INSERT_CODE_COMMAND, result.data as string);
  },

  keywords: [],

  // Determine if a given URL is a match and return url data.
  parseUrl: async (url: string) => {
    return null;
  },

  type: 'embed-iframe',
};

export const EmbedConfigs = [
  YoutubeEmbedConfig,
  CodeEmbedConfig,
  // TwitterEmbedConfig,
  // FigmaEmbedConfig,
];

function AutoEmbedMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: AutoEmbedOption;
}) {
  let className = 'item';
  if (isSelected) {
    className += ' selected';
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <span className="text">{option.title}</span>
    </li>
  );
}

function AutoEmbedMenu({
  options,
  selectedItemIndex,
  onOptionClick,
  onOptionMouseEnter,
}: {
  selectedItemIndex: number | null;
  onOptionClick: (option: AutoEmbedOption, index: number) => void;
  onOptionMouseEnter: (index: number) => void;
  options: Array<AutoEmbedOption>;
}) {
  return (
    <div className="typeahead-popover">
      <ul>
        {options.map((option: AutoEmbedOption, i: number) => (
          <AutoEmbedMenuItem
            index={i}
            isSelected={selectedItemIndex === i}
            onClick={() => onOptionClick(option, i)}
            onMouseEnter={() => onOptionMouseEnter(i)}
            key={option.key}
            option={option}
          />
        ))}
      </ul>
    </div>
  );
}

const debounce = (callback: (text: string) => void, delay: number) => {
  let timeoutId: number;
  return (text: string) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(text);
    }, delay);
  };
};

export function AutoEmbedDialog({
  embedConfig,
  onClose,
}: {
  embedConfig: PlaygroundEmbedConfig;
  onClose: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [editor] = useLexicalComposerContext();
  const [embedResult, setEmbedResult] = useState<EmbedMatchResult | null>(null);
  const [matchError, setMatchError] = useState<string>('');
  const { t } = useTranslation();

  const validateText = useMemo(
    () =>
      debounce((inputText: string) => {
        if (embedConfig.type === 'embed-iframe') {
          if (inputText != null) {
            if (validateIframeWithParser(inputText)) {
              setEmbedResult({ id: 'embed-iframe', url: '', data: inputText });
              setMatchError('');
            } else {
              setEmbedResult(null);
            }
          } else if (embedResult != null) {
            setEmbedResult(null);
          }
        } else {
          const urlMatch = URL_MATCHER.exec(inputText);
          if (embedConfig != null && inputText != null && urlMatch != null) {
            Promise.resolve(embedConfig.parseUrl(inputText)).then(
              (parseResult) => {
                if (parseResult) {
                  setEmbedResult(parseResult);
                  setMatchError('');
                } else {
                  setEmbedResult(parseResult);
                  setMatchError(t('YOUTUBE URL이 아닙니다.'));
                }
              },
            );
          } else if (embedResult != null) {
            setMatchError(t('YOUTUBE URL을 입력해주세요.'));
            setEmbedResult(null);
          } else {
            console.log('inputText', embedResult, inputText);
            setMatchError(t('YOUTUBE URL을 입력해주세요.'));
          }
        }
      }, 200),
    [embedConfig, embedResult],
  );

  const validateIframeWithParser = (input: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(input, 'text/html');
    const iframe = doc.querySelector('iframe');

    // 최상위 태그가 iframe인지 확인
    if (
      doc.body.children.length !== 1 ||
      doc.body.firstElementChild?.tagName.toLowerCase() !== 'iframe'
    ) {
      setMatchError(t('iframe만 최상위 태그로 입력 가능합니다.'));
      return false;
    }

    if (!iframe) {
      setMatchError(t('iframe만 입력 가능합니다.'));
      return false;
    }

    // 금지된 태그 이름 목록
    const forbiddenTags = ['script'];

    // 금지된 태그 검색
    for (const tag of forbiddenTags) {
      if (doc.querySelector(tag)) {
        setMatchError(t('{{tag}} 태그는 허용되지 않습니다.', { tag }));
        return false;
      }
    }

    // 금지된 속성 확인
    const forbiddenAttributes = ['srcdoc', 'onload', 'onerror'];
    for (const attr of forbiddenAttributes) {
      if (iframe.hasAttribute(attr)) {
        setMatchError(t('iframe에 "{{attr}}" 속성은 허용되지 않습니다.', { attr }));
        return false;
      }
    }

    // 입력 문자열에서 iframe 외의 내용이 포함되어 있는지 확인
    const sanitizedInput = input.trim();
    const iframeHtml = iframe.outerHTML.trim();
    if (sanitizedInput !== iframeHtml) {
      setMatchError(t('iframe 외부에 다른 콘텐츠가 포함될 수 없습니다.'));
      return false;
    }

    // iframe 내부 콘텐츠 검증
    const iframeContent = iframe.innerHTML;
    if (iframeContent) {
      const iframeContentDoc = parser.parseFromString(
        iframeContent,
        'text/html',
      );
      if (iframeContentDoc.querySelector('script')) {
        setMatchError(t('iframe 내부에 script 태그를 포함할 수 없습니다.'));
        return false;
      }
    }

    const width = iframe.getAttribute('width');
    const height = iframe.getAttribute('height');

    // if (!/^\d+px$/.test(width || '')) {
    //   setMatchError('width는 px 단위의 숫자여야 합니다.');
    //   return false;
    // }
    // if (!/^\d+px$/.test(height || '')) {
    //   setMatchError('height는 px 단위의 숫자여야 합니다.');
    //   return false;
    // }

    return true;
  };

  const onClick = () => {
    if (embedResult != null) {
      embedConfig.insertNode(editor, embedResult);
      onClose();
    }
  };

  return (
    <div style={{ width: 600, maxWidth: '100%' }}>
      <div className="Input__wrapper">
        {embedConfig.type === 'embed-iframe' ? (
          <textarea
            className="Input__input"
            placeholder={embedConfig.exampleUrl}
            value={text}
            data-test-id={`${embedConfig.type}-embed-modal-url`}
            style={{ height: '150px' }}
            onChange={(e) => {
              const { value } = e.target;
              setText(value);
              validateText(value);
            }}
          ></textarea>
        ) : (
          <input
            type="text"
            className="Input__input"
            placeholder={embedConfig.exampleUrl}
            value={text}
            data-test-id={`${embedConfig.type}-embed-modal-url`}
            onChange={(e) => {
              const { value } = e.target;
              setText(value);
              validateText(value);
            }}
          />
        )}
      </div>
      <DialogActions>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant={'body2'} color={'var(--dk-danger, #F55060)'} fontWeight={'300'}>
            {matchError && <>*{matchError}</>}
          </Typography>
          <Button
            disabled={!embedResult}
            onClick={onClick}
            data-test-id={`${embedConfig.type}-embed-modal-submit-btn`}
          >
            {t('삽입')}
          </Button>
        </Stack>
      </DialogActions>
    </div>
  );
}

export default function AutoEmbedPlugin(): JSX.Element {
  const [modal, showModal] = useModal();
  const { t } = useTranslation();

  const openEmbedModal = (embedConfig: PlaygroundEmbedConfig) => {
    showModal(
      t('{{name}} 삽입', { name: t(embedConfig.contentName) }),
      (onClose) => (
        <AutoEmbedDialog embedConfig={embedConfig} onClose={onClose} />
      ),
      false,
    );
  };

  const getMenuOptions = (
    activeEmbedConfig: PlaygroundEmbedConfig,
    embedFn: () => void,
    dismissFn: () => void,
  ) => {
    return [
      new AutoEmbedOption(t('취소'), {
        onSelect: dismissFn,
      }),
      new AutoEmbedOption(
        t('{{name}} 삽입', { name: t(activeEmbedConfig.contentName) }),
        {
          onSelect: embedFn,
        },
      ),
    ];
  };

  return (
    <>
      {modal}
      <LexicalAutoEmbedPlugin<PlaygroundEmbedConfig>
        embedConfigs={EmbedConfigs}
        onOpenEmbedModalForConfig={openEmbedModal}
        getMenuOptions={getMenuOptions}
        menuRenderFn={(
          anchorElementRef,
          {
            selectedIndex,
            options,
            selectOptionAndCleanUp,
            setHighlightedIndex,
          },
        ) =>
          anchorElementRef.current
            ? ReactDOM.createPortal(
                <div
                  className="typeahead-popover auto-embed-menu"
                  style={{
                    marginLeft: anchorElementRef.current.style.width,
                    width: 200,
                  }}
                >
                  <AutoEmbedMenu
                    options={options}
                    selectedItemIndex={selectedIndex}
                    onOptionClick={(option: AutoEmbedOption, index: number) => {
                      setHighlightedIndex(index);
                      selectOptionAndCleanUp(option);
                    }}
                    onOptionMouseEnter={(index: number) => {
                      setHighlightedIndex(index);
                    }}
                  />
                </div>,
                anchorElementRef.current,
              )
            : null
        }
      />
    </>
  );
}
